import _round from 'lodash/round';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { getProductTypeDisplayName, getProductTypeIcon } from '../summary/OrderSummaryGrid';
import { hideHeight, showHeight } from '../summary/OrderSummaryOptions';

import { createStyles, createStyle } from 'utils/createStyle';
import { monetizeWithDecimal } from 'common/utils/strings';
import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import {
    getPaymentDisplayName,
    getProductName,
    getWalletCardDisplayName,
} from 'features/order-summary/utils';
import { calculatePaymentFee } from 'features/order-summary/utils/useCartDetails';
import { ShipToHome } from 'components/custom-icons';
import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import { PlaceOrderResponse, ShippingOption } from 'common/api/e-comm/orders/service';
import { PaymentTypeFee } from 'common/api/e-comm/payment-types';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { EGiftCard, GiftCard } from 'common/api/e-comm/models/Card';
import { FeatureFlag } from 'components/feature-flag';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useSelector } from 'react-redux';
import { selectIsCAOrganization } from 'common/features/store/duck/home/duck';

export interface OrderSuccessGridData {
    data: PlaceOrderResponse;
    cart: CartResponse;
    shipToHome: boolean;
    selectedShipping: ShippingOption | undefined;
    paymentFee: PaymentTypeFee | undefined;
    total: number;
    shippingFee: number;
}

export interface OrderSuccessGridProps {
    data: OrderSuccessGridData;
}

const styles = createStyles({
    grid: [
        'grid border border-solid border-grey-3 overflow-hidden max-w-3xl',
        {
            borderRadius: '10px',
            gridTemplateColumns: '1px 3rem 1fr 1fr 1fr 1fr 1fr 1px',
            columnGap: '1rem',
        },
    ],

    gridTotal: [
        'grid col-span-8 border-t border-solid border-grey-3',
        {
            gridTemplateColumns: 'inherit',
            columnGap: 'inherit',
            background: '#F7F7F7',
        },
    ],

    message: 'text-lg lg:text-xl text-center col-span-8 text-success font-bold py-3',
    column: 'text-xs font-bold text-brand-dark py-2',

    container: createStyle(''),
    title: ['text-brand', { fontSize: '17px' }],
    description: 'text-sm text-grey-1',
    subtotal: 'text-grey-1 flex flex-row align-center',
    subtotalItems: createStyle('grid', {
        gridTemplateColumns: 'max-content auto',
    }),
    area: [
        'mb-2',
        {
            overflow: 'hidden',
            animationName: showHeight,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
    hidden: {
        overflow: 'hidden',
        animationName: hideHeight,
        animationDuration: '.5s',
        animationTimingFunction: EASE_IN_OUT_BACK,
        animationFillMode: 'forwards',
    },
});

export const renderItems = (items: CartLineItem[], walletCards: (GiftCard | EGiftCard)[]) =>
    items.map((item) => (
        <React.Fragment key={item.id}>
            <div className="col-span-8 py-1" />

            <div />
            <div className="flex items-center justify-center text-grey-2">
                {<img src={item.brand?.imageUrl} />}
            </div>
            <div className="text-sm truncate">
                <div className="flex text-lg text-brand-dark align-middle truncate relative">
                    <span className="flex items-center float-left mr-1">
                        {getProductTypeIcon(item.product)}
                    </span>
                    <span className="text-sm inline-block align-text-bottom font-bold truncate w-full">
                        {getProductName(item)}
                    </span>
                </div>
                <div className="text-xs text-grey-1 truncate">
                    {getProductTypeDisplayName(item.product)}
                    <FeatureFlag flagKey="enable_custom_card_names" fallback={true}>
                        {getWalletCardDisplayName(item, walletCards)
                            ? ` - ${getWalletCardDisplayName(item, walletCards)}`
                            : ''}
                    </FeatureFlag>
                </div>
            </div>
            <div className="text-xs flex items-center justify-center text-center text-grey-1">
                <span className="truncate">
                    Earn {_round((item.earnings / item.subtotal) * 100, 2).toFixed(2)}%
                </span>
                <span>&nbsp;=&nbsp;</span>
                <span className="text-success font-bold">{monetizeWithDecimal(item.earnings)}</span>
            </div>
            <div className="flex items-center justify-center text-center text-sm font-bold text-grey-1">
                {monetizeWithDecimal(item.price || 0)}
            </div>
            <div className="flex items-center justify-center text-center text-sm text-grey-1">
                {item.quantity}
            </div>
            <div className="flex items-center justify-center text-center text-sm text-brand-dark font-bold">
                {monetizeWithDecimal(item.subtotal || 0)}
            </div>
            <div />

            <div className="col-span-8 py-1" />
        </React.Fragment>
    ));

const renderItemsMobile = (items: CartLineItem[], walletCards: (GiftCard | EGiftCard)[]) =>
    items.map((item) => {
        return (
            <React.Fragment key={item.id}>
                <div key={item.id} className={'grid py-4 truncate col-span-8 px-2'}>
                    <div className="text-sm truncate">
                        <div className="text-sm truncate">
                            <div className="flex">
                                <div className="flex text-sm text-brand-dark align-middle truncate relative">
                                    <span className="flex items-center float-left mr-1">
                                        {getProductTypeIcon(item.product)}
                                    </span>
                                    <span className="inline-block align-text-bottom font-bold truncate w-full">
                                        {getProductName(item)}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-1 text-xs text-grey-1">
                                {getProductTypeDisplayName(item.product)}
                                {item.product.isShipToHomeEligible && (
                                    <ShipToHome height="0.6rem" className="inline-block ml-2" />
                                )}
                                <FeatureFlag flagKey="enable_custom_card_names" fallback={true}>
                                    {getWalletCardDisplayName(item, walletCards)
                                        ? ` - ${getWalletCardDisplayName(item, walletCards)}`
                                        : ''}
                                </FeatureFlag>
                            </div>
                            <div>
                                <div className="flex">
                                    <div className="text-xs mr-auto text-grey-1 inline-block truncate">
                                        Earn{' '}
                                        {_round((item.earnings / item.subtotal) * 100, 2).toFixed(
                                            2
                                        )}
                                        % ={' '}
                                        <span className="text-success">
                                            {monetizeWithDecimal(item.earnings)}
                                        </span>
                                    </div>
                                    <div className="ml-auto text-right inline-block text-center text-sm text-grey-1">
                                        <span className="ml-4">{item.quantity.toString()}</span>
                                        {' x '}
                                        <span>{monetizeWithDecimal(item.price)}</span>
                                    </div>
                                </div>
                                <div className="flex justify-end text-right inline-block text-sm font-bold text-brand-dark">
                                    Amount {monetizeWithDecimal(item.subtotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    });

export const OrderSuccessGrid = ({ data }: OrderSuccessGridProps) => {
    const { cart, shipToHome, selectedShipping, paymentFee, total, shippingFee } = data;
    const { lineItems } = cart;
    const isCAOrganization = useSelector(selectIsCAOrganization);

    const walletCards = useCardsFromWallet();

    const [showMobileLayout, setShowMobileLayout] = useState(window.innerWidth <= 1280);

    const earn = lineItems.reduce((acc, item) => acc + item.earnings, 0);

    const renderTotalPayment = () => {
        if (isCAOrganization) {
            return null;
        }
        return (
            <>
                <div />
                <div className="text-xs text-grey-1 flex flex-row items-center col-span-4">
                    {paymentFee &&
                        paymentFee.amount > 0 &&
                        getPaymentDisplayName(isCAOrganization, paymentFee.paymentType)}{' '}
                </div>
                <div className="text-xs text-center flex flex-row items-center justify-center" />
                <div className="text-sm text-center flex flex-row items-center justify-center pb-1">
                    {monetizeWithDecimal(
                        calculatePaymentFee(cart.subtotal + shippingFee, paymentFee)
                    )}
                </div>
                <div />
            </>
        );
    };

    const hasPhysicalCards = lineItems.some((item) => item.product.type === ProductType.GiftCard);

    const renderTotal = () => {
        return (
            <>
                <div className="border-t border-solid border-grey-3 col-span-8 pb-3" />

                <div />
                <div className="text-xs text-grey-1 flex flex-row items-center col-span-4">
                    Subtotal
                </div>
                <div className="text-xs text-center flex flex-row items-center justify-center">
                    {cart?.productCount || 0}
                </div>
                <div className="text-sm text-center flex flex-row items-center justify-center font-bold pb-1">
                    {cart && monetizeWithDecimal(cart.subtotal)}
                </div>
                <div />

                {renderTotalPayment()}

                {shipToHome && hasPhysicalCards && (
                    <>
                        <div />
                        <div className="text-xs text-grey-1 flex flex-row items-center col-span-4">
                            Shipping &amp; Handling
                        </div>
                        <div className="text-xs text-center flex flex-row items-center justify-center" />
                        <div className="text-sm text-center flex flex-row items-center justify-center pb-1">
                            {selectedShipping ? monetizeWithDecimal(selectedShipping.cost) : '-'}
                        </div>
                        <div />
                    </>
                )}

                <div className="col-span-8 pt-3" />

                <div className={styles.gridTotal}>
                    <div className="col-span-5" />
                    <div className="font-bold text-xs text-center flex items-center justify-center">
                        Total
                    </div>
                    <div className="font-bold text-sm text-center text-brand-dark flex items-center justify-center py-3">
                        {monetizeWithDecimal(
                            isCAOrganization ? cart.subtotal + shippingFee : total
                        )}
                    </div>
                    <div />
                </div>
            </>
        );
    };

    useEffect(() => {
        const fn = () => setShowMobileLayout(window.innerWidth <= 1280);

        window.addEventListener('resize', fn);

        return () => window.removeEventListener('resize', fn);
    }, []);

    return (
        <>
            <div />
            <div className={styles.grid}>
                <div className={styles.message}>
                    — You earned {monetizeWithDecimal(earn)} on this order —
                </div>

                {!showMobileLayout && (
                    <>
                        <div className={classNames(styles.column)} />
                        <div className={classNames(styles.column, 'text-left')} />
                        <div className={classNames(styles.column, 'text-left')}>Product</div>
                        <div className={classNames(styles.column, 'text-center')}>Earnings</div>
                        <div className={classNames(styles.column, 'text-center')}>Value</div>
                        <div className={classNames(styles.column, 'text-center')}>Quantity</div>
                        <div className={classNames(styles.column, 'text-center')}>
                            Dollar Amount
                        </div>
                        <div className={classNames(styles.column)} />
                    </>
                )}

                <div className="col-span-8 border-b border-solid border-grey-3" />

                <div />
                {!showMobileLayout && renderItems(lineItems, walletCards)}
                {showMobileLayout && renderItemsMobile(lineItems, walletCards)}

                {renderTotal()}
            </div>
        </>
    );
};
